/* ---------------------------------------------------------------------------
   Installing — the service worker, and the application itself.

   Two things wear that word here, and both belong in one file because they are
   the same subject seen twice: what it takes for this to keep working on a
   device that has walked out of signal. The first half registers the worker.
   The second half is THE INSTALL PROMPT, further down — the captured browser
   event that lets Options offer a button instead of a paragraph explaining
   where a menu item lives on somebody else’s phone.

   Registering the service worker, carefully.

   The worker itself is public/sw.js and its strategies are documented there.
   This is the other half: when to register it, and how not to make the
   application unfixable if it turns out to be wrong.

   **Never in development.** `vite dev` serves unhashed modules and rewrites them
   on every edit; a worker caching that is a debugging session spent chasing
   stale code. Registered only from a built, served-over-https origin.

   **Never before first paint.** Registration competes with the initial fetch of
   the register for the same connection, and the app being usable matters more
   than it being installable a second earlier.

   **An escape hatch that does not need a deploy.** `?sw=off` unregisters the
   worker and clears every cache it owns. If a bad worker ever ships, that URL is
   the fix a person can be told over the phone — which matters, because the
   normal remedy is "clear site data in devtools" and an inspector on a district
   phone will not be doing that.
   --------------------------------------------------------------------------- */

/** True when this build is being served the way a real deployment is. */
function eligible(): boolean {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
  // Secure context, or localhost, which browsers treat as one.
  if (!window.isSecureContext) return false;
  /* VITE'S DEV SERVER, ON WHICHEVER PORT IT CAME UP ON. This used to read
     `port === '5173'`, which was the whole of the dev server as long as there was
     one — and then run-philippines.bat and run-pakistan.bat brought up 5273 and
     5274, where the test passed and a worker was registered against a dev origin.
     Its shell cache then answered navigations from a build somebody had moved on
     from, which is the "sometimes it works" a dev server must never produce.

     `import.meta.hot` is defined by Vite in dev and replaced with undefined in a
     build, so it says the thing the port was standing in for. Read through a cast
     because this project does not pull in vite/client's types, and reached as a
     plain property so the type-stripped test runner — which has no import.meta.env
     at all — sees undefined rather than throwing. */
  if ((import.meta as { hot?: unknown }).hot) return false;
  return true;
}

/**
 * Tear the worker out and clear its caches.
 *
 * Exported so `?sw=off` can call it, and so a future "reset this device" action
 * has something honest to call rather than reimplementing it.
 */
export async function unregisterWorker(): Promise<void> {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    const names = await caches.keys();
    await Promise.all(names.filter((n) => n.startsWith('bw-')).map((n) => caches.delete(n)));
  } catch {
    /* Nothing registered, or the browser refused. Either way there is no worker
       to be rid of, which is the outcome the caller wanted. */
  }
}

/**
 * Install the worker, or remove it if the escape hatch is in the URL.
 *
 * Deliberately returns nothing and never throws: a browser that refuses to
 * register a worker is a browser the app still has to work in, and there is
 * nothing useful for a caller to do about it.
 */
export function startServiceWorker(): void {
  if (typeof window === 'undefined') return;

  if (new URLSearchParams(window.location.search).get('sw') === 'off') {
    void unregisterWorker().then(() => {
      // Say so plainly. Somebody has been told to visit this URL because the app
      // was broken, and silence looks like it did not work.
      console.info('[bridge-watch] service worker removed and caches cleared.');
    });
    return;
  }

  if (!eligible()) return;

  // After load, so registration never competes with the first render.
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('./sw.js', { scope: './' }).catch(() => {
      /* An origin that will not take a worker still runs the app; it simply does
         not work offline, which the known-gaps section says. */
    });
  });
}

/* ===========================================================================
   THE INSTALL PROMPT

   The application has been installable since GR-03. Nothing in it ever said so.
   An inspector was expected to find "Install app" in a Chrome menu, or Share →
   Add to Home Screen in Safari, having been told which by somebody — and the
   guide did tell them, in four steps, which is four more than a button.

   ONE RULE, AND IT IS THE ONE OPTIONS IS ALREADY BUILT ON: **only offer what the
   browser has actually agreed to.** Chromium fires `beforeinstallprompt` when,
   and only when, it is willing to honour a prompt — the manifest passed, the
   worker is in place, the app is not already installed. Held, that event IS the
   button. Not held, there is nothing to press, and drawing a button anyway
   produces the one outcome worse than no button: an inspector taps Install,
   nothing happens, and they conclude the application is broken.

   So there is no synthesised prompt and no guess. Where the event exists there
   is a button; where it does not — iOS Safari, Firefox, an app already on the
   home screen — Options names the menu that holds it instead.

   NO BANNER, EITHER. The browser's own mini-infobar is suppressed and nothing
   replaces it. An offer that interrupts is an offer made at the moment somebody
   is doing something else, and the something else here is standing at a
   riverbank. The offer sits on Options and waits to be found.
   =========================================================================== */

/**
 * The event Chromium fires when it will honour an install.
 *
 * Declared here because it is in no specification and therefore not in
 * lib.dom.d.ts. Only `prompt` and `userChoice` are used; `platforms` is written
 * down because reading it is how a later caller would learn what it is being
 * offered on.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

/** What this device can be told about installing — measured, never assumed. */
export interface Installability {
  /**
   * A prompt is in hand, and pressing a button will show it.
   *
   * False is not "cannot be installed". It is "this browser has not offered, so
   * we do not know", which is why the copy for it names a menu rather than
   * claiming anything.
   */
  canPrompt: boolean;
  /**
   * This is running as an installed app, or the browser said it has just
   * installed one. Two separate measurements, one conclusion, and both are
   * evidence rather than inference — see `isStandalone`.
   */
  installed: boolean;
}

/** How to install by hand, where there is no prompt to capture. */
export type InstallRoute = 'ios' | 'menu';

/** The captured event, or null. Spendable exactly once. */
let deferred: BeforeInstallPromptEvent | null = null;
/** True once `appinstalled` has fired in this tab. */
let installedSeen = false;
/** Who to tell. Set by watchInstall, which the boot sequence calls once. */
let announce: ((state: Installability) => void) | null = null;

function report(): void {
  announce?.({ canPrompt: deferred !== null, installed: installedSeen || isStandalone() });
}

/**
 * True when this page is running from a home-screen icon rather than in a tab.
 *
 * `display-mode: standalone` and nothing else, and the omissions are the care in
 * this function. `fullscreen` matches a browser tab somebody has pressed F11 in,
 * so testing it would report a desktop Chrome window as an installed app.
 * `minimal-ui` is a display mode this manifest never asks for, so a match could
 * not have come from installing this.
 *
 * `navigator.standalone` is the iOS half. Safari fires no `beforeinstallprompt`
 * and predates display-mode, so this is the only way to know an iPhone is
 * running the installed copy — which matters, because it is the one platform
 * where instructions are all Options has to give.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    /* No matchMedia. Not installed as far as anything here can tell, which is
       the honest answer rather than the convenient one. */
    return false;
  }
}

/**
 * Which set of by-hand instructions to print.
 *
 * Two, not five. The distinction that matters is Safari's Share sheet against
 * everybody else's menu; naming Firefox or Samsung Internet specifically would
 * be more words for the same instruction.
 *
 * iPadOS 13 and later report themselves as Macintosh, so the touch count is what
 * separates an iPad from a desktop Mac. A desktop Mac reading the iOS sentence
 * would be told to use a Share sheet that will not install anything.
 */
export function installRoute(): InstallRoute {
  if (typeof navigator === 'undefined') return 'menu';
  const ua = navigator.userAgent;
  const ios =
    /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
  return ios ? 'ios' : 'menu';
}

/**
 * Start watching, and report every change.
 *
 * Called once, early at boot — earlier than it strictly needs to be. The event
 * cannot fire before the worker is registered, but a listener attached after it
 * fired is a listener that never hears it, and attaching one costs a function
 * call.
 *
 * Reports once immediately, so a device already running the installed copy says
 * so on the first paint rather than after an event that will never arrive.
 */
export function watchInstall(onChange: (state: Installability) => void): void {
  if (typeof window === 'undefined') return;
  announce = onChange;

  window.addEventListener('beforeinstallprompt', (e: Event) => {
    /* preventDefault, so the browser's mini-infobar stays down and the offer is
       made once, in the place that explains it. Without this a phone gets both —
       the bar at the bottom and the card on Options — which reads as two
       different offers rather than one. */
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    report();
  });

  window.addEventListener('appinstalled', () => {
    /* Spent. The browser will not offer again for an app it has just installed,
       so a button left on screen would be one that cannot work. */
    deferred = null;
    installedSeen = true;
    report();
  });

  try {
    // Chrome can move the current tab into the installed window, which changes
    // the answer without reloading anything.
    window.matchMedia('(display-mode: standalone)').addEventListener('change', report);
  } catch {
    /* An older Safari, whose MediaQueryList takes addListener instead. The
       immediate report below still stands, and `navigator.standalone` answers
       the question on that browser anyway. */
  }

  report();
}

/**
 * Show the captured prompt, and say what happened.
 *
 * `unavailable` covers both nothing-to-show and the browser refusing, because a
 * caller can do nothing different about the two: either way no install happened,
 * and the by-hand instructions are what is left to offer.
 */
export async function showInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';

  /* Cleared first, and unconditionally. The event may be prompted exactly once —
     a second call throws — so a button that survived the first press would
     report a browser error as a failure to install. A dismissal is not
     permanent: reloading the page fires a fresh event, which is what the note on
     the screen tells somebody to do. */
  deferred = null;
  report();

  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return 'unavailable';
  }
}
